// src/lib/api_analytics.ts
// Cliente para cross-channel analytics overview (F1).

const DJANGO_PROXY = '/django-api';

import { ensureFreshToken } from '@/lib/tokenRefresh';

async function getJwt(): Promise<string> {
  await ensureFreshToken();
  const token = localStorage.getItem('auth_token');
  if (!token) throw new Error('Sesión expirada. Inicia sesión nuevamente.');
  return token;
}

async function authHeaders(): Promise<Record<string, string>> {
  return {
    Authorization: `Bearer ${await getJwt()}`,
  };
}

export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

async function djangoFetch<T = any>(path: string, init?: RequestInit): Promise<T> {
  const headers: Record<string, string> = await authHeaders();
  if (init?.body !== undefined && !(init?.headers && (init.headers as Record<string, string>)['Content-Type'])) { headers['Content-Type'] = 'application/json'; }
  const res = await fetch(`${DJANGO_PROXY}${path}`, {
    ...init, headers: { ...headers, ...(init?.headers || {}) },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const detail = err.detail || `Django ${res.status}: ${res.statusText}`;
    if (res.status === 401) throw new ApiError('Sesión expirada.', 401);
    throw new ApiError(typeof detail === 'string' ? detail : JSON.stringify(detail), res.status);
  }
  return res.json();
}

// ═══════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════

export type ChannelKey = 'meta' | 'google_ads' | 'whatsapp' | 'publish' | 'ai_marketing';

export interface ChannelTotals {
  spend_cents: number | null;
  cost_micros: number | null;
  impressions: number | null;
  clicks: number | null;
  conversions: number | null;
  reach: number | null;
  inbound: number | null;
  outbound_sent: number | null;
  outbound_failed: number | null;
  broadcasts_completed: number | null;
  published: number | null;
  scheduled: number | null;
  content_generated: number | null;
  audiences_suggested: number | null;
  alerts_generated: number | null;
}

export interface DailyPoint {
  date: string;
  channel: string;
  impressions: number;
  clicks: number;
  spend_cents: number;
  conversions: number;
}

export interface AnalyticsOverview {
  window_start: string;
  window_end: string;
  channels: Record<ChannelKey, ChannelTotals>;
  timeseries: DailyPoint[];
}

export async function getOverview(days = 7): Promise<AnalyticsOverview> {
  return djangoFetch<AnalyticsOverview>(
    `/api/v1/admin/analytics/overview?days=${days}`,
  );
}

// ═══════════════════════════════════════════════════════════════
// CM-1 KPI endpoints (summary / trends / campaigns / roi / sources)
// ═══════════════════════════════════════════════════════════════

export interface KpiSummary {
  window_start: string;
  window_end: string;
  impressions: number;
  clicks: number;
  spend_cents: number;
  conversions: number;
  revenue_cents: number;
  reach: number;
  ctr: number | null;
  cpc_cents: number | null;
  cpm_cents: number | null;
  roas: number | null;
  cvr: number | null;
  cost_per_conversion_cents: number | null;
  frequency: number | null;
}

export interface KpiTrendPoint {
  date: string;
  impressions: number;
  clicks: number;
  spend_cents: number;
  conversions: number;
  revenue_cents: number;
  reach: number;
  ctr: number | null;
  cpc_cents: number | null;
  cpm_cents: number | null;
  roas: number | null;
  cvr: number | null;
  cost_per_conversion_cents: number | null;
  frequency: number | null;
}

export interface CampaignKpi {
  campaign_id: number;
  campaign_name: string;
  status: string;
  objective: string;
  impressions: number;
  clicks: number;
  spend_cents: number;
  conversions: number;
  revenue_cents: number;
  reach: number;
  ctr: number | null;
  cpc_cents: number | null;
  cpm_cents: number | null;
  roas: number | null;
  cvr: number | null;
  cost_per_conversion_cents: number | null;
  frequency: number | null;
}

export interface RoiSummary {
  window_start: string;
  window_end: string;
  total_spend_cents: number;
  total_revenue_cents: number;
  total_conversions: number;
  roas: number | null;
  cost_per_conversion_cents: number | null;
  by_campaign: CampaignKpi[];
}

export interface SourceBreakdown {
  window_start: string;
  window_end: string;
  sources: Record<string, KpiSummary>;
}

export async function getSummary(days = 7, campaignId?: number): Promise<KpiSummary> {
  const qs = campaignId ? `?days=${days}&campaign_id=${campaignId}` : `?days=${days}`;
  return djangoFetch<KpiSummary>(`/api/v1/admin/analytics/summary${qs}`);
}

export async function getTrends(days = 30, campaignId?: number): Promise<KpiTrendPoint[]> {
  const qs = campaignId ? `?days=${days}&campaign_id=${campaignId}` : `?days=${days}`;
  return djangoFetch<KpiTrendPoint[]>(`/api/v1/admin/analytics/trends${qs}`);
}

export async function getCampaignCards(days = 30, limit = 100): Promise<CampaignKpi[]> {
  return djangoFetch<CampaignKpi[]>(
    `/api/v1/admin/analytics/campaigns?days=${days}&limit=${limit}`,
  );
}

export async function getRoi(days = 30): Promise<RoiSummary> {
  return djangoFetch<RoiSummary>(`/api/v1/admin/analytics/roi?days=${days}`);
}

export async function getSourceBreakdown(days = 30): Promise<SourceBreakdown> {
  return djangoFetch<SourceBreakdown>(`/api/v1/admin/analytics/sources?days=${days}`);
}

// ═══════════════════════════════════════════════════════════════
// CM-2a — Campaign alerts (read + ack)
// ═══════════════════════════════════════════════════════════════

export interface CampaignAlert {
  id: number;
  org_id: string;
  campaign_id: number;
  campaign_name: string;
  ad_id: number | null;
  kind: string;
  severity: 'info' | 'warning' | 'critical';
  message: string;
  context: Record<string, any>;
  detected_at: string;
  resolved_at: string | null;
  resolved_by: string;
}

export interface PaginatedAlerts {
  items: CampaignAlert[];
  count: number;
}

export async function listAlerts(params: {
  unresolved_only?: boolean;
  severity?: 'info' | 'warning' | 'critical';
  kind?: string;
  campaign_id?: number;
} = {}): Promise<PaginatedAlerts> {
  const qs = new URLSearchParams();
  if (params.unresolved_only !== undefined) qs.set('unresolved_only', String(params.unresolved_only));
  if (params.severity) qs.set('severity', params.severity);
  if (params.kind) qs.set('kind', params.kind);
  if (params.campaign_id) qs.set('campaign_id', String(params.campaign_id));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return djangoFetch<PaginatedAlerts>(`/api/v1/admin/campaigns/alerts${suffix}`);
}

export async function resolveAlert(alertId: number): Promise<CampaignAlert> {
  return djangoFetch<CampaignAlert>(
    `/api/v1/admin/campaigns/alerts/${alertId}/resolve`, { method: 'POST' },
  );
}

// ═══════════════════════════════════════════════════════════════
// Format helpers
// ═══════════════════════════════════════════════════════════════

export function centsToCurrency(cents: number | null, currency = 'USD', locale = 'es-MX'): string {
  if (cents == null) return '—';
  return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(cents / 100);
}

export function microsToCurrency(micros: number | null, currency = 'MXN', locale = 'es-MX'): string {
  if (micros == null) return '—';
  return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(micros / 1_000_000);
}

export function formatInt(n: number | null): string {
  if (n == null) return '—';
  return new Intl.NumberFormat('es-MX').format(n);
}

export function formatPercent(v: number | null, decimals = 2): string {
  if (v == null) return '—';
  return `${v.toFixed(decimals)}%`;
}

export function formatRatio(v: number | null, decimals = 2): string {
  if (v == null) return '—';
  return v.toFixed(decimals);
}
